import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssignmentIcon from "@mui/icons-material/Assignment";
import RoomIcon from "@mui/icons-material/Room";
import CloseIcon from "@mui/icons-material/Close";
import { useMaterialReactTable, MaterialReactTable } from "material-react-table";
import type { MRT_ColumnDef } from "material-react-table";
import styled from "@emotion/styled";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { doc, getDoc } from "firebase/firestore";
import type { ChipProps } from "@mui/material/Chip";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useShipmentManager } from "../../context/ShipmentManagerProvider";
import { useUserData } from "../../context/UserDataFetcher";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { CustomThemeType } from "../../context/Providers";
import type { ItemSnapshot, Shipment, Order } from "../../types/DB2";
import {
  collectOrderDisplayData,
  OrderDisplayData,
  stripOrderPrefix,
} from "../../utils/ordersDisplay";
import { formatStatusLabel } from "../../utils/status";
import {
  getItemStatusChipColor,
  getItemStatusMarkerIcon,
  getOrderStatusChipColor,
  getOrderStatusMarkerIcon,
  normalizeStatusValue,
} from "../../utils/statusColors";
import { db } from "../../firebaseConfig";
import config from "../../config";
import { normalizeOrderItemsSnapshot } from "../../utils/normalizeSnapshots";
import ShipmentsMap, { MapShipment } from "../../components/ShipmentsMap/ShipmentsMap";
import { darkMapStyle, lightMapStyle } from "../../components/ShipmentsMap/mapStyles";
import { useShipmentGeocodes } from "../../utils/geocode";

const CalendarWrapper = styled.div<{ theme?: CustomThemeType }>`
  & .rdrDefinedRangesWrapper {
    display: none;
  }
  & .rdrCalendarWrapper {
    background-color: ${({ theme }) => theme?.background};
    border: 2px solid ${({ theme }) => theme?.dividerColor};
  }
  & .rdrDateDisplayWrapper {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrStartEdge,
  & .rdrInRange,
  & .rdrEndEdge,
  & .rdrSelected {
    color: ${({ theme }) => theme?.background} !important;
  }
  & .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
  & .rdrDayNumber span::after {
    background: ${({ theme }) => theme?.foreground};
  }
  & .rdrMonthName,
  & .rdrWeekDay {
    color: ${({ theme }) => theme?.brand};
  }
  & .rdrDay {
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrNextPrevButton i {
    color: ${({ theme }) => theme?.foreground};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrMonthAndYearPickers select {
    color: ${({ theme }) => theme?.brand};
    background-color: ${({ theme }) => theme?.mainBackground};
  }
  & .rdrPprevButton i {
    border-color: transparent ${({ theme }) => theme?.brand} transparent transparent;
  }
  & .rdrNextButton i {
    border-color: transparent transparent transparent ${({ theme }) => theme?.brand};
  }
  & .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
  & .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
    color: ${({ theme }) => theme?.foreground};
  }
`;

const getShipmentDate = (shipment: Shipment): Date | null => {
  const raw = shipment.shipmentDate as unknown;
  if (!raw) return null;
  if (typeof (raw as any)?.toDate === "function") {
    return (raw as any).toDate();
  }
  if (raw instanceof Date) return raw;
  if (typeof raw === "string") {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof raw === "number") {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

type GeoPointLike = { latitude?: number; longitude?: number } | null | undefined;

const toLatLng = (geo: GeoPointLike): { lat: number; lng: number } | null => {
  if (!geo) return null;
  const lat = typeof geo?.latitude === "number" ? geo.latitude : undefined;
  const lng = typeof geo?.longitude === "number" ? geo.longitude : undefined;
  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }
  return null;
};

const resolveOrderLocation = (
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
): { lat?: number; lng?: number; formattedAddress?: string } => {
  const destinationsGeo =
    (order.snapshot?.destinationsGeo
      ? Object.values(order.snapshot.destinationsGeo)
      : []) ?? [];
  const preservedDestinationsGeo =
    (order.snapshot?.preservedOrder?.destinationsGeo
      ? Object.values(order.snapshot.preservedOrder.destinationsGeo)
      : []) ?? [];

  const geoCandidates = [...destinationsGeo, ...preservedDestinationsGeo];
  for (const geo of geoCandidates) {
    const latLng = toLatLng(geo as GeoPointLike);
    if (latLng) return latLng;
  }

  for (const item of order.includedItems) {
    const latLng = toLatLng((item as any)?.dropoffGeo as GeoPointLike);
    if (latLng) return { ...latLng };
  }

  if (shipmentLocation) {
    return {
      lat: shipmentLocation.lat,
      lng: shipmentLocation.lng,
      formattedAddress: shipmentLocation.formattedAddress,
    };
  }

  return {};
};

const resolveItemLocation = (
  item: ItemSnapshot,
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string }
): { lat?: number; lng?: number; formattedAddress?: string } => {
  const itemLatLng = toLatLng((item as any)?.dropoffGeo as GeoPointLike);
  if (itemLatLng) {
    return itemLatLng;
  }

  const orderLocation = resolveOrderLocation(order, shipmentLocation);
  if (orderLocation.lat && orderLocation.lng) {
    return orderLocation;
  }

  if (shipmentLocation) {
    return {
      lat: shipmentLocation.lat,
      lng: shipmentLocation.lng,
      formattedAddress: shipmentLocation.formattedAddress,
    };
  }

  return {};
};

const buildOrderMapData = (
  shipment: Shipment,
  order: OrderDisplayData,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string },
  options?: { iconUrl?: string }
) => {
  const orderLocation = resolveOrderLocation(order, shipmentLocation);
  const shipmentDate = getShipmentDate(shipment);
  const addressSummary =
    orderLocation.formattedAddress ||
    order.destinations[0] ||
    shipmentLocation?.formattedAddress;

  const mapShipment: MapShipment = {
    id: `${shipment.id}-${order.orderId}`,
    status: shipment.status,
    label: `Order ${stripOrderPrefix(order.orderId, order.snapshot)}`,
    lat: orderLocation.lat,
    lng: orderLocation.lng,
    subtitle: shipmentDate ? `Shipment date: ${format(shipmentDate, "PP")}` : undefined,
    addressSummary,
    detailLines: [
      `Shipment: ${shipment.id}`,
      `Dealer: ${order.dealerLabel || "Unknown dealer"}`,
      `Included items: ${order.includedItems.length}`,
      `Excluded items: ${order.excludedItems.length}`,
      `Destinations: ${order.destinations.length || "None"}`,
    ],
    orders: [
      {
        orderId: order.orderId,
        itemCount: order.includedItems.length,
        destinations: order.destinations,
      },
    ],
    iconUrl: options?.iconUrl,
  };

  return {
    mapShipment,
    addressSummary,
  };
};

const buildItemMapData = (
  shipment: Shipment,
  order: OrderDisplayData,
  item: ItemSnapshot,
  shipmentLocation?: { lat: number; lng: number; formattedAddress?: string },
  options?: { iconUrl?: string }
) => {
  const itemLocation = resolveItemLocation(item, order, shipmentLocation);
  const shipmentDate = getShipmentDate(shipment);
  const cabinetLabel = item.cabinetNumber ? String(item.cabinetNumber).trim() : "";
  const itemLabel = cabinetLabel.length
    ? `Cabinet ${cabinetLabel}`
    : item.modelName || item.id;
  const addressSummary =
    itemLocation.formattedAddress ||
    item.destination ||
    order.destinations[0] ||
    shipmentLocation?.formattedAddress;

  const mapShipment: MapShipment = {
    id: `${shipment.id}-${order.orderId}-${item.id}`,
    status: shipment.status,
    label: itemLabel,
    lat: itemLocation.lat,
    lng: itemLocation.lng,
    subtitle: shipmentDate ? `Shipment date: ${format(shipmentDate, "PP")}` : undefined,
    addressSummary,
    detailLines: [
      `Shipment: ${shipment.id}`,
      `Order: ${order.orderId}`,
      `Item status: ${formatStatusLabel(item.status) || item.status || "Unknown"}`,
      cabinetLabel.length ? `Cabinet #: ${cabinetLabel}` : undefined,
      item.destination ? `Destination: ${item.destination}` : undefined,
    ].filter(Boolean) as string[],
    orders: [
      {
        orderId: order.orderId,
        itemCount: 1,
        destinations: order.destinations,
      },
    ],
    iconUrl: options?.iconUrl,
  };

  return {
    mapShipment,
    addressSummary,
  };
};


type AggregatedOrder = {
  orderId: string;
  orderLabel: string;
  rawStatus?: string;
  statusLabel: string;
  statusKey?: string;
  statusChipColor: ChipProps["color"];
  statusMarkerIcon?: string;
  dealerLabel: string;
  destinations: string[];
  totalIncluded: number;
  totalExcluded: number;
  shipments: Array<{
    shipment: Shipment;
    orderData: OrderDisplayData;
    shipmentDate: Date | null;
  }>;
  shipmentIds: string[];
  lastShipmentDate: Date | null;
};

type OrderMapDialogContext =
  | {
      kind: "order";
      shipment: Shipment;
      order: OrderDisplayData;
      statusKey?: string;
      markerIcon?: string;
    }
  | {
      kind: "item";
      shipment: Shipment;
      order: OrderDisplayData;
      item: ItemSnapshot;
      itemType: "included" | "excluded";
      statusKey?: string;
      markerIcon?: string;
    };

type MapDialogState =
  | { open: false }
  | { open: true; context: OrderMapDialogContext };

type OrderMapDialogData = {
  kind: "order";
  title: string;
  subtitle?: string;
  mapShipments: MapShipment[];
  shipment: Shipment;
  order: OrderDisplayData;
  statusKey?: string;
};

type ItemMapDialogData = {
  kind: "item";
  title: string;
  subtitle?: string;
  mapShipments: MapShipment[];
  shipment: Shipment;
  order: OrderDisplayData;
  item: ItemSnapshot;
  itemType: "included" | "excluded";
  statusKey?: string;
};

type ActiveDialogData = OrderMapDialogData | ItemMapDialogData | null;

const renderItemsList = (
  items: ItemSnapshot[],
  heading: string,
  options?: {
    canOpenMap?: (item: ItemSnapshot) => boolean;
    onOpenMap?: (item: ItemSnapshot) => void;
  }
) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
      {heading}
    </Typography>
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem key={item.id} disableGutters sx={{ alignItems: "flex-start", py: 0.75 }}>
          <Inventory2Icon fontSize="small" sx={{ color: "text.secondary", mr: 1, mt: 0.5 }} />
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                {item.cabinetNumber && (
                  <Typography variant="body2" fontWeight={600}>
                    Cabinet # {item.cabinetNumber}
                  </Typography>
                )}
                <Chip
                  size="small"
                  color={getItemStatusChipColor(item.status)}
                  label={formatStatusLabel(item.status) || item.status || ""}
                />
                <Typography variant="body2">
                  {item.modelName || item.description || item.id}
                </Typography>
                {options?.onOpenMap && options?.canOpenMap?.(item) ? (
                  <Tooltip title="View on map">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          options.onOpenMap?.(item);
                        }}
                        sx={{ ml: 0.5 }}
                      >
                        <RoomIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" display="block">
                Destination: {item.destination || "Not provided"}
              </Typography>
            }
          />
        </ListItem>
      ))}
      {!items.length && (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 4, py: 0.5 }}>
          No items in this bucket.
        </Typography>
      )}
    </List>
  </Box>
);

const ViewOrders: React.FC = () => {
  const { shipments, dateRange, setDateRange, refreshShipments } = useShipmentManager();
  const userData = useUserData();
  const { orders: dealerOrders, shipments: dealerShipments } = usePrimaryFetchedData();
  const muiTheme = useMuiTheme();
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [adminOrders, setAdminOrders] = useState<Order[]>([]);
  const [mapDialogState, setMapDialogState] = useState<MapDialogState>({ open: false });
  const dbPrefix = config.productionDbPrefix;
  const mapStyles = useMemo(
    () => (muiTheme.palette.mode === "dark" ? darkMapStyle : lightMapStyle),
    [muiTheme.palette.mode]
  );

  const userEmail = (userData as any)?.email as string | undefined;
  const isDealer = userData?.role === "dealer";
  const shipmentsForAggregation = useMemo<Shipment[]>(
    () =>
      isDealer
        ? (Array.isArray(dealerShipments) ? dealerShipments : [])
        : Array.isArray(shipments)
        ? shipments
        : [],
    [dealerShipments, isDealer, shipments]
  );
  const { locations, pending: geoPending } = useShipmentGeocodes({
    shipments: shipmentsForAggregation,
  });
  const getShipmentLocation = useCallback(
    (shipmentId: string) => {
      const record = locations[shipmentId];
      if (!record) return undefined;
      return {
        lat: record.lat,
        lng: record.lng,
        formattedAddress: record.formattedAddress,
      };
    },
    [locations]
  );

  const canShowOrderMap = useCallback(
    (shipment: Shipment, order: OrderDisplayData) => {
      const candidate = resolveOrderLocation(order, getShipmentLocation(shipment.id));
      return typeof candidate.lat === "number" && typeof candidate.lng === "number";
    },
    [getShipmentLocation]
  );

  const canShowItemMap = useCallback(
    (shipment: Shipment, order: OrderDisplayData, item: ItemSnapshot) => {
      const candidate = resolveItemLocation(item, order, getShipmentLocation(shipment.id));
      return typeof candidate.lat === "number" && typeof candidate.lng === "number";
    },
    [getShipmentLocation]
  );

  const handleOpenOrderMap = useCallback(
    (shipment: Shipment, order: OrderDisplayData, statusKey?: string, markerIcon?: string) => {
      setMapDialogState({
        open: true,
        context: { kind: "order", shipment, order, statusKey, markerIcon },
      });
    },
    []
  );

  const handleOpenItemMap = useCallback(
    (
      shipment: Shipment,
      order: OrderDisplayData,
      item: ItemSnapshot,
      itemType: "included" | "excluded"
    ) => {
      const statusKey = normalizeStatusValue(item.status);
      const markerIcon = getItemStatusMarkerIcon(item.status);
      setMapDialogState({
        open: true,
        context: { kind: "item", shipment, order, item, itemType, statusKey, markerIcon },
      });
    },
    []
  );

  const closeMapDialog = useCallback(() => {
    setMapDialogState({ open: false });
  }, []);

  useEffect(() => {
    if (isDealer) {
      setAdminOrders([]);
      return;
    }

    const shipmentsSource = Array.isArray(shipments) ? shipments : [];
    const orderIds = Array.from(
      new Set(
        shipmentsSource.flatMap((shipment) =>
          Object.keys(shipment?.ordersSnapshot ?? {}).map((orderId) => orderId || "").filter(Boolean)
        )
      )
    );

    if (!orderIds.length) {
      setAdminOrders([]);
      return;
    }

    let cancelled = false;
    const fetchOrders = async () => {
      try {
        const fetched: Order[] = [];
        await Promise.all(
          orderIds.map(async (orderId) => {
            try {
              const ref = doc(db, `${dbPrefix}orders`, orderId);
              const snapshot = await getDoc(ref);
              if (!snapshot.exists()) return;
              const data = snapshot.data() as Order;
              const normalizedItems = data.itemsSnapshot
                ? normalizeOrderItemsSnapshot(orderId, data.itemsSnapshot, "ViewOrders:adminFetch")
                : undefined;
              fetched.push({
                ...data,
                id: data.id || orderId,
                ...(normalizedItems ? { itemsSnapshot: normalizedItems } : {}),
              });
            } catch (error) {
              console.warn("[ViewOrders] Failed to fetch order", { orderId, error });
            }
          })
        );
        if (!cancelled) {
          setAdminOrders(fetched);
        }
      } catch (error) {
        console.warn("[ViewOrders] Failed to fetch canonical orders", error);
        if (!cancelled) {
          setAdminOrders([]);
        }
      }
    };

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [dbPrefix, isDealer, shipments]);

  const aggregatedOrders = useMemo<AggregatedOrder[]>(() => {
    const sourceOrders = (isDealer ? dealerOrders : adminOrders) ?? [];
    if (!Array.isArray(sourceOrders) || !sourceOrders.length) return [];
    const viewerDealerId = userData?.dealerId ?? "";

    return (sourceOrders as Order[])
      .map((order) => {
        const orderId = String(order?.id ?? (order as any)?.orderId ?? "");
        const shipmentsForOrder = shipmentsForAggregation.filter(
          (shipment) => shipment?.ordersSnapshot && shipment.ordersSnapshot[orderId]
        );

        let includedItems: ItemSnapshot[] = [];
        let excludedItems: ItemSnapshot[] = [];

        const shipmentsDetail = shipmentsForOrder.map((shipment) => {
          const orderDisplays = collectOrderDisplayData(shipment);
          const orderDisplay = orderDisplays.find((display) => display.orderId === orderId) ?? null;
          if (orderDisplay) {
            includedItems = includedItems.concat(orderDisplay.includedItems);
            excludedItems = excludedItems.concat(orderDisplay.excludedItems);
          }
          return {
            shipment,
            orderData: orderDisplay ?? {
              orderId,
              snapshot: null,
              includedItems: [],
              excludedItems: [],
              destinations: [],
              dealerLabel: order.dealerId || viewerDealerId || "",
            },
            shipmentDate: getShipmentDate(shipment),
          };
        });

        if (!includedItems.length && order?.itemsSnapshot) {
          includedItems = Object.values(order.itemsSnapshot) as ItemSnapshot[];
        }

        const destinations = new Set<string>();
        shipmentsDetail.forEach(({ orderData }) => {
          orderData?.destinations?.forEach((dest) => {
            if (typeof dest === "string" && dest.trim()) destinations.add(dest.trim());
          });
        });
        if (Array.isArray((order as any)?.destinations)) {
          (order as any).destinations.forEach((dest: string) => {
            if (typeof dest === "string" && dest.trim()) destinations.add(dest.trim());
          });
        }

      const rawStatus: string | undefined = (order as any)?.status;
      const statusKey = normalizeStatusValue(rawStatus);
      const formattedStatus =
        rawStatus?.length ? formatStatusLabel(rawStatus) || rawStatus : "Unknown";
      const statusChipColor = getOrderStatusChipColor(rawStatus);
      const statusMarkerIcon = getOrderStatusMarkerIcon(rawStatus);

        const lastShipmentDate = shipmentsDetail.reduce<Date | null>((latest, detail) => {
          if (!detail.shipmentDate) return latest;
          if (!latest || detail.shipmentDate.getTime() > latest.getTime()) {
            return detail.shipmentDate;
          }
          return latest;
        }, null);

        return {
          orderId,
        orderLabel: stripOrderPrefix(orderId, shipmentsDetail[0]?.orderData?.snapshot),
        rawStatus,
        statusLabel: formattedStatus,
        statusKey,
        statusChipColor,
        statusMarkerIcon,
        dealerLabel: order.dealerId || viewerDealerId || "",
          destinations: Array.from(destinations),
          totalIncluded: includedItems.length,
          totalExcluded: excludedItems.length,
          shipments: shipmentsDetail,
          shipmentIds: shipmentsDetail.map((detail) => detail.shipment.id),
          lastShipmentDate,
        };
      })
      .sort((a, b) => a.orderLabel.localeCompare(b.orderLabel));
  }, [adminOrders, dealerOrders, isDealer, shipmentsForAggregation, userData?.dealerId]);

  const orders = aggregatedOrders;
  const mapDialogData = useMemo<ActiveDialogData>(() => {
    if (!mapDialogState.open) return null;
    const context = mapDialogState.context;
    const shipmentLocation = getShipmentLocation(context.shipment.id);
    const markerIcon = context.markerIcon || getOrderStatusMarkerIcon(context.statusKey);

    if (context.kind === "order") {
      const { mapShipment, addressSummary } = buildOrderMapData(
        context.shipment,
        context.order,
        shipmentLocation,
        { iconUrl: markerIcon }
      );
      return {
        kind: "order",
        title: `Order ${stripOrderPrefix(context.order.orderId, context.order.snapshot)}`,
        subtitle: addressSummary || context.order.destinations[0] || undefined,
        mapShipments: [mapShipment],
        shipment: context.shipment,
        order: context.order,
        statusKey: context.statusKey,
      };
    }

    const { mapShipment, addressSummary } = buildItemMapData(
      context.shipment,
      context.order,
      context.item,
      shipmentLocation,
      { iconUrl: markerIcon }
    );
    return {
      kind: "item",
      title: context.item.modelName || context.item.id,
      subtitle: addressSummary || context.item.destination || undefined,
      mapShipments: [mapShipment],
      shipment: context.shipment,
      order: context.order,
      item: context.item,
      itemType: context.itemType,
      statusKey: context.statusKey,
    };
  }, [getShipmentLocation, mapDialogState]);

  const dialogShipments = mapDialogData?.mapShipments ?? [];
  const mapShipmentsWithCoords = useMemo(
    () =>
      dialogShipments.filter(
        (entry) => typeof entry.lat === "number" && typeof entry.lng === "number"
      ),
    [dialogShipments]
  );
  const hasCoordinates = mapShipmentsWithCoords.length > 0;
  const selectedShipmentIdForMap = hasCoordinates ? mapShipmentsWithCoords[0]?.id ?? null : null;
  const dialogLoading = mapDialogState.open && geoPending;

  const renderMapDialogInfo = () => {
    if (!mapDialogData) return null;
    if (mapDialogData.kind === "order") {
      const statusLabel =
        formatStatusLabel(mapDialogData.statusKey) ||
        mapDialogData.statusKey ||
        "Unknown";
      const chipColor = getOrderStatusChipColor(mapDialogData.statusKey);
      return (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Order Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shipment {mapDialogData.shipment.id}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" color={chipColor} label={statusLabel} />
            <Chip
              size="small"
              variant="outlined"
              label={`Included: ${mapDialogData.order.includedItems.length}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Excluded: ${mapDialogData.order.excludedItems.length}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Destinations: ${mapDialogData.order.destinations.length}`}
            />
          </Stack>
          <Typography variant="body2">
            Dealer: {mapDialogData.order.dealerLabel || "Unknown"}
          </Typography>
          <Typography variant="body2">
            Destinations:{" "}
            {mapDialogData.order.destinations.length
              ? mapDialogData.order.destinations.join(", ")
              : "No destinations recorded"}
          </Typography>
        </Stack>
      );
    }

    const cabinetLabel = mapDialogData.item.cabinetNumber
      ? String(mapDialogData.item.cabinetNumber)
      : "";
    const itemStatusLabel =
      formatStatusLabel(mapDialogData.item.status) ||
      mapDialogData.item.status ||
      "Unknown";
    const itemChipColor = getItemStatusChipColor(mapDialogData.item.status);
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Item Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shipment {mapDialogData.shipment.id} · Order{" "}
            {stripOrderPrefix(mapDialogData.order.orderId, mapDialogData.order.snapshot)}
          </Typography>
        </Box>
        <Chip size="small" color={itemChipColor} label={itemStatusLabel} sx={{ alignSelf: "flex-start" }} />
        <Typography variant="body2">
          Label: {mapDialogData.item.modelName || mapDialogData.item.id}
        </Typography>
        {cabinetLabel.length ? (
          <Typography variant="body2">Cabinet #: {cabinetLabel}</Typography>
        ) : null}
        <Typography variant="body2">Status: {itemStatusLabel}</Typography>
        <Typography variant="body2">
          Destination: {mapDialogData.item.destination || "Not provided"}
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    try {
      const shipmentSource = isDealer
        ? (Array.isArray(dealerShipments) ? dealerShipments : [])
        : (shipments ?? []);
      const sampleShipmentIds = shipmentSource.slice(0, 3).map((s) => s?.id ?? null);
      const sampleOrderIds = orders.slice(0, 3).map((o) => o?.orderId ?? null);
      console.debug("[DealerDebug][ViewOrders] render summary", {
        role: userData?.role,
        dealerId: userData?.dealerId,
        email: userEmail,
        isAdmin: userData?.isAdmin,
        shipmentsCount: shipmentSource.length,
        ordersCount: orders.length,
        source: isDealer ? "dealerOrders" : "shipments",
        sampleShipmentIds,
        sampleOrderIds,
        dateRangeStart: dateRange?.startDate?.toISOString?.(),
        dateRangeEnd: dateRange?.endDate?.toISOString?.(),
      });
    } catch (error) {
      console.warn("[DealerDebug][ViewOrders] failed to log summary", error);
    }
  }, [shipments, dealerShipments, orders, userData?.role, userData?.dealerId, userEmail, dateRange, isDealer]);

  const headCellSx = useMemo(
    () => ({ px: 0.75, py: 0.5, whiteSpace: "nowrap", verticalAlign: "middle" as const }),
    []
  );
  const bodyCellSx = useMemo(
    () => ({ px: 0.75, py: 0.9, verticalAlign: "middle" as const }),
    []
  );

  const formatRangeLabel = () => {
    const { startDate, endDate } = dateRange || ({} as any);
    if (!startDate || !endDate) return "Select Date Range";
    try {
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
    } catch {
      return "Select Date Range";
    }
  };

  const columns = useMemo<MRT_ColumnDef<AggregatedOrder>[]>(
    () => [
      {
        accessorKey: "orderLabel",
        header: "Order",
        size: 200,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              Order {row.original.orderLabel}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "statusLabel",
        header: "Status",
        size: 140,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={row.original.statusChipColor}
            label={row.original.statusLabel}
          />
        ),
      },
      {
        accessorKey: "dealerLabel",
        header: "Dealer",
        size: 200,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
      },
      {
        header: "Shipments",
        accessorFn: (row) => row.shipmentIds.length,
        size: 140,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {row.original.shipments.map(({ shipment }) => (
              <Chip
                key={shipment.id}
                size="small"
                icon={<LocalShippingIcon fontSize="small" />}
                label={`${shipment.id}`}
                variant="outlined"
              />
            ))}
          </Stack>
        ),
      },
      {
        header: "Items",
        accessorFn: (row) => row.totalIncluded + row.totalExcluded,
        size: 140,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.totalIncluded} included / {row.original.totalExcluded} excluded
          </Typography>
        ),
      },
      {
        header: "Destinations",
        accessorFn: (row) => row.destinations.join(", "),
        size: 220,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {row.original.destinations.length
              ? row.original.destinations.join(", ")
              : "No destinations"}
          </Typography>
        ),
      },
      {
        header: "Last Shipment",
        accessorFn: (row) => row.lastShipmentDate ? row.lastShipmentDate.getTime() : null,
        sortingFn: "basic",
        size: 160,
        muiTableHeadCellProps: { sx: headCellSx },
        muiTableBodyCellProps: { sx: bodyCellSx },
        Cell: ({ row }) => {
          const date = row.original.lastShipmentDate;
          if (!date) return <Typography variant="body2">Unknown</Typography>;
          return (
            <Typography variant="body2">
              {format(date, "PP")}
            </Typography>
          );
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: orders,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    initialState: {
      showGlobalFilter: false,
      density: "comfortable",
      pagination: {
        pageSize: 50,
        pageIndex: 0,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => row.toggleExpanded(),
      sx: {
        "&:hover": {
          cursor: "pointer",
          backgroundColor: muiTheme.palette.action.hover,
        },
      },
    }),
    muiTopToolbarProps: {
      sx: (theme) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        "& > div": {
          flexDirection: "row-reverse",
        },
      }),
    },
    renderTopToolbarCustomActions: () => (
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CalendarMonthIcon />}
        onClick={(e) => setCalendarAnchor(e.currentTarget)}
        sx={{
          backgroundColor: "background.paper",
          borderColor: "divider",
          color: "primary.main",
          position: "relative",
          bottom: ".25rem",
          "&:hover, &:active": {
            backgroundColor: "background.paper",
            borderColor: "primary.main",
          },
        }}
      >
        {formatRangeLabel()}
      </Button>
    ),
    renderDetailPanel: ({ row }) => (
      <Box sx={{ p: 2, bgcolor: "background.default" }}>
        <Stack spacing={2}>
          {row.original.shipments.map(({ shipment, orderData, shipmentDate }) => (
            <Box
              key={`${shipment.id}-${orderData.orderId}`}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalShippingIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Shipment {shipment.id}
                  </Typography>
                  <Chip
                    size="small"
                    color="info"
                    label={formatStatusLabel(shipment.status) || shipment.status}
                  />
                  <Tooltip
                    title={
                      canShowOrderMap(shipment, orderData)
                        ? "View order on map"
                        : "No location data yet"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canShowOrderMap(shipment, orderData)}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenOrderMap(
                            shipment,
                            orderData,
                            row.original.statusKey,
                            row.original.statusMarkerIcon
                          );
                        }}
                      >
                        <RoomIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {shipmentDate ? format(shipmentDate, "PP") : "No scheduled date"}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Destinations:{" "}
                {orderData.destinations.length
                  ? orderData.destinations.join(", ")
                  : "No destinations recorded"}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Included: ${orderData.includedItems.length}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Excluded: ${orderData.excludedItems.length}`}
                />
              </Stack>
              <Divider sx={{ my: 2 }} />
              {renderItemsList(orderData.includedItems, "Included Items", {
                canOpenMap: (item) => canShowItemMap(shipment, orderData, item),
                onOpenMap: (item) => handleOpenItemMap(shipment, orderData, item, "included"),
              })}
              <Divider sx={{ my: 2 }} />
              {renderItemsList(orderData.excludedItems, "Excluded Items", {
                canOpenMap: (item) => canShowItemMap(shipment, orderData, item),
                onOpenMap: (item) => handleOpenItemMap(shipment, orderData, item, "excluded"),
              })}
            </Box>
          ))}
          {!row.original.shipments.length && (
            <Typography variant="body2" color="text.secondary">
              This order is not associated with any shipments in the selected date range.
            </Typography>
          )}
        </Stack>
      </Box>
    ),
  });

  const handleCloseCalendar = () => setCalendarAnchor(null);

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Box sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "1400px" }}>
          <Box
            sx={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "primary.main",
              }}
            >
              <AssignmentIcon
                sx={{ fontSize: "2.5rem", bottom: "2px", position: "relative" }}
                color="primary"
              />
              View Orders
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => refreshShipments()}>
                Refresh
              </Button>
            </Stack>
          </Box>

          <MaterialReactTable table={table} />

          <Popover
            open={Boolean(calendarAnchor)}
            anchorEl={calendarAnchor}
            onClose={handleCloseCalendar}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <CalendarWrapper>
              <DateRangePicker
                onChange={(item: any) => {
                  const range = item.range1 || item.selection || {};
                  setDateRange({
                    startDate: range.startDate ?? dateRange.startDate,
                    endDate: range.endDate ?? dateRange.endDate,
                    key: range.key || "selection",
                  });
                }}
                showPreview={false}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={[dateRange]}
                showDateDisplay={false}
                staticRanges={[]}
                inputRanges={[]}
                direction="horizontal"
              />
            </CalendarWrapper>
          </Popover>
          <Dialog
            open={mapDialogState.open && Boolean(mapDialogData)}
            onClose={closeMapDialog}
            maxWidth="lg"
            fullWidth
            aria-labelledby="order-map-dialog-title"
          >
            {mapDialogData ? (
              <>
                <DialogTitle
                  id="order-map-dialog-title"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {mapDialogData.title}
                    </Typography>
                    {mapDialogData.subtitle && (
                      <Typography variant="body2" color="text.secondary">
                        {mapDialogData.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={closeMapDialog}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    sx={{ minHeight: { xs: 520, md: 480 } }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: { xs: 320, md: 480 },
                        position: "relative",
                      }}
                    >
                      {hasCoordinates ? (
                        <ShipmentsMap
                          shipments={mapShipmentsWithCoords}
                          selectedShipmentId={selectedShipmentIdForMap}
                          mapStyles={mapStyles}
                          isLoading={dialogLoading}
                          initialZoom={14}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            p: 3,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No location data available for this selection.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: { xs: "100%", md: 360 },
                        borderTop: { xs: 1, md: 0 },
                        borderLeft: { md: 1 },
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        p: 3,
                      }}
                    >
                      {renderMapDialogInfo()}
                    </Box>
                  </Stack>
                </DialogContent>
              </>
            ) : null}
          </Dialog>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ViewOrders;
